// src/utils/carrierShortnameMap.js
//
// Carrier shortname map used by buildVaultPath() to construct spec-conformant
// filenames per FIGVault_Spec_v1.1 Rule 5.
//
// Primary key: NAIC code. Falls back to case-insensitive CompanyName lookup for
// carriers without a stable NAIC in our records (primarily Lloyd's syndicates,
// foreign insurers, and carriers we've placed via non-admitted paper).
//
// Entries were established during the 2026-04-19 migration (manifest v2) and
// reflect the F8/F9/F10/F11 shortname decisions approved by Marc.
//
// Adding a new carrier:
//   1. Prefer byNAIC for any carrier with a stable NAIC code.
//   2. Shortname must be filesystem-safe (no spaces, no punctuation except
//      internal capitalization boundaries). Aim for shortest unambiguous form.
//   3. If another carrier in the map starts with the same prefix, disambiguate
//      (see StarrSurplus vs StarrIndemnity, ChubbLloyds vs future Chubb entries).

export const CARRIER_SHORTNAMES_BY_NAIC = {
  // Admitted — FIG's live book
  '34690': 'HartfordPC',            // Property and Casualty Insurance Company of Hartford
  '16691': 'GreatAmericanInsurance', // Great American Insurance Company (disambig from GA Alliance/Assurance/etc.)
  '23035': 'LibertyMutualFire',     // Liberty Mutual Fire Insurance Company (disambig from Liberty Mutual Insurance Co 23043)
  '27774': 'ChubbLloyds',           // Chubb Lloyds Insurance Company of Texas (disambig from other Chubb entities)
  '29939': 'TexasMutual',           // Texas Mutual Insurance Company
  // Surplus lines — FIG's live book
  '10172': 'Westchester',           // Westchester Surplus Lines Insurance Company (per spec example 3)
  '11045': 'MSIG',                  // MSIG Specialty Insurance USA Inc.
  '13027': 'Covington',             // Covington Specialty Insurance Company
  '13604': 'StarrSurplus',          // Starr Surplus Lines Insurance Company (disambig from Starr Indemnity)
  '19429': 'ICSOP',                 // Insurance Company of the State of Pennsylvania (industry-standard abbreviation)
  '19437': 'Lexington',             // Lexington Insurance Company
  '35378': 'Evanston',              // Evanston Insurance Company (per spec example 1; TDI entity behind Covington Specialty program)
  '36056': 'Navigators',            // Navigators Specialty Insurance Company
  '38318': 'StarrIndemnity',        // Starr Indemnity & Liability Company (disambig from StarrSurplus)
  '39993': 'ColonyInsurance',       // Colony Insurance Company (disambig from Colony Specialty 36927)
  '44768': 'Homeland',              // Homeland Insurance Company of New York
  '48127': 'Kinsale',               // Kinsale Insurance Company
};

// Fallback by case-insensitive CompanyName. Keys are lowercased. Used when
// policy.NAICCode is missing/invalid on the FIG_Policies record but policy.Carrier
// has the legal entity string.
//
// Includes:
//   (a) Carriers without a stable NAIC in use (Lloyd's, Markel Intl, a few
//       Admitted carriers whose NAIC is consistently blank on our records).
//   (b) Every canonical CompanyName from byNAIC above — so that if the NAIC
//       field is blank but the Carrier string is populated (common for older
//       records and Personal Lines policies), the resolver still hits a
//       map entry and doesn't fall through to the derived fallback.
//       (Policy #53 Mark Daves Chubb Lloyds surfaced this gap on 2026-04-20.)
export const CARRIER_SHORTNAMES_BY_NAME = {
  // --- (a) No-NAIC carriers ---
  // Lloyd's syndicate placements — no stable NAIC (AA-coded or unnumbered)
  "underwriters at lloyd's of london": 'Lloyds',
  "certain underwriters at lloyd's, london": 'Lloyds',
  // Foreign insurers — no US NAIC
  'markel international insurance company limited': 'MarkelIntl',
  // Admitted carriers whose NAIC isn't populated in FIG_Policies today
  'travelers casualty and surety company of america': 'TravelersCasualtySurety',
  'berkley assurance company': 'BerkleyAssurance',
  'progressive county mutual insurance company': 'ProgressiveCountyMutual',
  'admiral insurance company': 'AdmiralInsurance',

  // --- (b) Canonical names mirroring byNAIC (NAIC-first still wins when present) ---
  'property and casualty insurance company of hartford': 'HartfordPC',
  'great american insurance company': 'GreatAmericanInsurance',
  'liberty mutual fire insurance company': 'LibertyMutualFire',
  'chubb lloyds insurance company of texas': 'ChubbLloyds',
  'texas mutual insurance company': 'TexasMutual',
  'westchester surplus lines insurance company': 'Westchester',
  'msig specialty insurance usa inc.': 'MSIG',
  'covington specialty insurance company': 'Covington',
  'starr surplus lines insurance company': 'StarrSurplus',
  'starr surplus lines ins co': 'StarrSurplus', // historical uppercase/abbreviated form seen on Policy #1
  'insurance company of the state of pennsylvania': 'ICSOP',
  'lexington insurance company': 'Lexington',
  'evanston insurance company': 'Evanston',
  'navigators specialty insurance company': 'Navigators',
  'starr indemnity & liability company': 'StarrIndemnity',
  'colony insurance company': 'ColonyInsurance',
  'homeland insurance company of new york': 'Homeland',
  'kinsale insurance company': 'Kinsale',
};

/**
 * Resolve a carrier to its FIGVault shortname.
 *
 * Resolution order (per Marc's 2026-04-20 directive):
 *   1. Hardcoded map by NAIC (authoritative).
 *   2. Hardcoded map by CompanyName (fallback for no-NAIC carriers).
 *   3. FIG_Carriers consistency check — if carriersList provided and NAIC resolves
 *      to a different CompanyName than policyCarrier, emit CARRIER_NAIC_DIVERGENCE
 *      warning but keep hardcoded map value.
 *   4. Derive from policyCarrier string by stripping spaces/punctuation. Emit
 *      CARRIER_SHORT_DERIVED warning.
 *   5. Return null.
 *
 * @param {Object}  args
 * @param {string=} args.policyCarrier  — legal entity string from policy.Carrier
 * @param {string=} args.policyNAIC     — NAIC code from policy.NAICCode
 * @param {Array=}  args.carriersList   — optional FIG_Carriers normalized array
 *                                         (shape: [{ NAICCode, CompanyName, ... }])
 * @returns {{ shortname: string|null, source: 'naic-map'|'name-map'|'derived'|null, warnings: Array<{code, message}> }}
 */
export function resolveCarrierShortname({ policyCarrier, policyNAIC, carriersList } = {}) {
  const warnings = [];
  const normalizedNAIC = policyNAIC ? String(policyNAIC).trim() : '';
  const normalizedName = policyCarrier ? String(policyCarrier).trim() : '';

  // 1. Primary: byNAIC
  if (normalizedNAIC && CARRIER_SHORTNAMES_BY_NAIC[normalizedNAIC]) {
    const shortname = CARRIER_SHORTNAMES_BY_NAIC[normalizedNAIC];

    // Consistency check against FIG_Carriers (non-authoritative)
    if (Array.isArray(carriersList) && normalizedName) {
      const carrierListEntry = carriersList.find(c => String(c?.NAICCode || '').trim() === normalizedNAIC);
      if (carrierListEntry && carrierListEntry.CompanyName) {
        const listNameLower = String(carrierListEntry.CompanyName).trim().toLowerCase();
        const policyNameLower = normalizedName.toLowerCase();
        if (listNameLower !== policyNameLower) {
          warnings.push({
            code: 'CARRIER_NAIC_DIVERGENCE',
            message: `FIG_Carriers NAIC ${normalizedNAIC} has CompanyName "${carrierListEntry.CompanyName}" but policy.Carrier is "${policyCarrier}". Using hardcoded map value "${shortname}".`,
          });
        }
      }
    }

    return { shortname, source: 'naic-map', warnings };
  }

  // 2. Fallback: byName
  if (normalizedName) {
    const lookupKey = normalizedName.toLowerCase();
    if (CARRIER_SHORTNAMES_BY_NAME[lookupKey]) {
      return { shortname: CARRIER_SHORTNAMES_BY_NAME[lookupKey], source: 'name-map', warnings };
    }
  }

  // 3. Derived fallback
  if (normalizedName) {
    const derived = normalizedName.replace(/[^A-Za-z0-9]/g, '');
    if (derived) {
      warnings.push({
        code: 'CARRIER_SHORT_DERIVED',
        message: `Carrier "${policyCarrier}" not in shortname map; derived "${derived}" by stripping non-alphanumerics. Add to carrierShortnameMap.js for stable naming.`,
      });
      return { shortname: derived, source: 'derived', warnings };
    }
  }

  // 4. Unresolvable
  return { shortname: null, source: null, warnings };
}
